<template>
  <div
    :id="questionId"
    :class="questionClass"
    :type="attributeType"
    class="questionModel"
    @clearValue="reset()"
    @disableInput="disableInput()"
    @enableInput="enableInput()"
    @enableRequired="enableRequired()"
    @removeRequired="removeRequired()"
  >
    <b-overlay :show="loading" rounded="sm">
      <b-card :aria-hidden="loading ? 'true' : null">
        <b-row class="mx-0">
          <b-col cols="10">
            <label class="questionLabelStyle"
              ><span v-if="isRequired" class="requiredStar">* </span>{{ label }}</label
            >
            <div v-if="helpText">
              <p class="helpTextStyle"><span v-html="helpText"></span></p>
            </div>
          </b-col>
          <b-col class="pr-0">
            <response-history
              v-show="documentAnswerId"
              :questionId="questionId"
              :survey-type="validationsType"
              :type="'document'"
            />
          </b-col>
        </b-row>
        <b-badge
          v-for="(file, index) in files"
          :key="'file_' + index"
          class="px-2 py-2 mr-2"
          variant="primary"
        >
          {{ file.name }}
          <b-icon
            class="ml-2"
            icon="x"
            scale="1.8"
            style="color: red;cursor:pointer;"
            @click="removeFile(index)"
          ></b-icon>
        </b-badge>
        <b-form-group :invalid-feedback="error">
          <b-form-file
            :id="questionId + '_value'"
            v-model="tempFiles"
            :accept="acceptedFileTypes.toString()"
            :disabled="disableDocument"
            :name="name"
            :state="formStatus"
            class="mb-2 mt-2"
            drop-placeholder="Drop one or more files here..."
            multiple
            placeholder="Use the browse button to select a file and then click upload"
            @input="addFile($event)"
          ></b-form-file>
          <div>
            <b-button
              :disabled="disabled"
              class="mr-1"
              size="sm"
              variant="primary"
              @click="upload()"
              >Upload
            </b-button>
            <b-button
              v-b-modal="'resetModal_' + this.questionId"
              class="mr-2"
              size="sm"
              variant="danger"
              >Reset
            </b-button>
            <span v-if="warningMessage"
              >This document will not be transmitted until you submit the form</span
            >
            <div v-if="showUrl" class="font-weight-bold">Uploaded files:</div>
            <div v-for="(item, index) in sharePointUrl" :key="item" class="px-2">
              <b-link class="mt-2" style="color: blue" @click="checkSecurity(item)"
                >{{ fileName[index] }}
              </b-link>
              <b-link @click="openDeleteWarning(fileName[index], index)">
                <b-icon icon="x" scale="1.5" style="color: red"></b-icon>
              </b-link>
            </div>
          </div>
        </b-form-group>
        <validation-observer ref="observer">
          <validation-provider
            ref="provider"
            v-slot="validationContext"
            :rules="`${isRequired ? 'required' : ''}`"
          >
            <b-form-input
              :id="questionId + '_hiddenInput'"
              ref="documentHiddenInput"
              v-model="validationModelInput"
              :state="getValidationState(validationContext)"
              aria-describedby="validationFeedback"
              disabled
              hidden
              readonly
              type="text"
            />
            <b-form-invalid-feedback id="validationFeedback"
              >{{ validationContext.errors[0] }}
            </b-form-invalid-feedback>
          </validation-provider>
        </validation-observer>
        <!-- <b-modal
          :id="'uploadModal_' + this.questionId"
          header-bg-variant="primary"
          header-text-variant="light"
          title="Uploading..."
          @ok="upload()"
        >
          <p class="my-4">
            This document is temporarily being stored and will not be saved until you save the
            {{ surveyOrForm }}.
          </p>
        </b-modal> -->
        <b-modal
          :id="'deleteModal_' + this.questionId"
          header-bg-variant="danger"
          header-text-variant="light"
          title="Deleting..."
          @ok="deleteDocument()"
        >
          <p class="my-4">
            <!-- Your file will be deleted from the database but, you will need to save the
            {{ surveyOrForm }} to complete the deletion of the URL. Are you sure that you want to
            proceed? -->
            The file selected will be deleted from your {{ surveyOrForm }}. Do you want to proceed?
          </p>
        </b-modal>
        <b-modal
          :id="'resetModal_' + this.questionId"
          header-bg-variant="danger"
          header-text-variant="light"
          title="Document resetting..."
          @ok="reset()"
        >
          <p class="my-4">
            <!-- This answer will be wiped and saving the {{ surveyOrForm }} in this state will remove
            any saved URLs from the database. In case you regret this option, please press cancel on
            the bottom of this page and reload the {{ surveyOrForm }}. Are you sure you want to
            reset? -->
            All attachments will be removed from this question on your {{ surveyOrForm }}. Do you
            want to reset?
          </p>
        </b-modal>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import ResponseHistory from './ResponseHistory.vue';

  export default {
    name: 'Documents',
    components: {
      ResponseHistory,
    },
    props: [
      'label',
      'questionId',
      'name',
      'answer',
      'objectId',
      'attributeType',
      'typeOfSurvey',
      'requiredQuestions',
      'validationsType',
      'helpText',
    ],
    data() {
      return {
        questionClass: {
          hideElement: false,
          'pl-5': false,
        },
        error: '',
        disabled: true,
        loading: false,
        files: [],
        tempFiles: null,
        formStatus: null,
        acceptedFileTypes: [
          'image/jpeg',
          'image/png',
          'application/pdf',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
          'application/vnd.ms-excel.addin.macroEnabled.12',
          'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
          'application/vnd.ms-excel.sheet.macroEnabled.12',
          'application/vnd.ms-excel.template.macroEnabled.12',
          'text/plain',
          'text/csv',
        ],
        acceptedFileSize: 4e6,
        sharePointUrl: [],
        sharePointResponse: null,
        showUrl: false,
        fileName: [],
        disableDocument: false,
        isRequired: false,
        itemToDelete: null,
        itemToDeleteIndex: null,
      };
    },
    created() {
      if (this.requiredQuestions.includes(this.questionId)) {
        this.isRequired = true;
      }
      if (this.answer !== undefined) {
        this.sharePointUrl = this.answer[0]['Saved_Value_Long__c']
          ? this.answer[0]['Saved_Value_Long__c'].split(',')
          : [];
        this.fileName = this.answer[0]['Saved_Value__c']
          ? this.answer[0]['Saved_Value__c'].split(',')
          : [];
        if (this.sharePointUrl.length > 0 && this.fileName.length > 0) {
          this.showUrl = true;
          this.$emit('input', {
            value: this.answer[0]['Saved_Value_Long__c'],
            id: this.documentAnswerId,
            fileName: this.answer[0]['Saved_Value__c'],
          });
        }
      }
    },
    mounted() {
      if (this.questionClass['hideElement']) {
        this.isRequired = false;
        this.$nextTick(() => {
          this.$refs.observer.reset();
        });
      }
    },
    computed: {
      ...mapGetters(['validationTrigger']),
      uploadStatus() {
        return this.sharePointUrl.length > 0;
      },
      surveyOrForm() {
        return this.$route.name === 'form-create' ? 'form' : 'survey';
      },
      documentAnswerId() {
        if (this.answer === undefined) {
          return undefined;
        } else {
          return this.answer[0]['Id'];
        }
      },
      validationModelInput() {
        return this.sharePointUrl.length > 0 ? 'valid' : '';
      },
      warningMessage() {
        return this.files.length > 0 || this.sharePointUrl.length > 0;
      },
    },
    watch: {
      files: function(newVal) {
        this.dirtySurvey(this.validationsType);
        if (!newVal) {
          this.formStatus = null;
          this.disabled = true;
        } else {
          this.disabled = false;
        }
      },
      async validationTrigger(newVal) {
        if (
          this.requiredQuestions.includes(this.questionId) &&
          newVal.includes(this.validationsType)
        ) {
          let data = [];
          data[0] = this.questionId;
          data[1] = await this.$refs.observer.validate();
          this.$store.commit(this.validationsType, data);
        }
      },
    },
    methods: {
      ...mapActions(['dirtySurvey']),
      addFile() {
        this.tempFiles.forEach(element => {
          this.files.push(element);
        });
        this.tempFiles = [];
      },
      removeFile(index) {
        this.files.splice(index, 1);
        this.tempFiles = [];
      },
      async upload() {
        if (this.files && this.checkSize() && this.checkType()) {
          this.loading = true;
          await this.uploadToSharePoint();
        } else {
          return;
        }
        this.loading = false;
      },
      async uploadToSharePoint() {
        let responses = [];
        for (let i = 0; i < this.files.length; i++) {
          let data = new FormData();
          let endpoint;
          data.append('file', this.files[i]);
          data.append('questionName', this.name);
          data.append('questionId', this.questionId);
          data.append('eventId', this.$route.params.eventId);
          endpoint = 'upload-document';

          await axios
            .post(`/api/${endpoint}`, data)
            .then(response => responses.push(response))
            .catch(error => {
              this.formStatus = false;
              this.handleError(
                error,
                'Something went wrong while uploading, please contact the support team.',
                `Error code: (${error.response.status})`,
              );
              this.loading = false;
            });
        }
        this.setUrl(responses);
      },
      setUrl(responses) {
        for (const response of responses) {
          if (response.data.statusCode === 200 || response.data.statusCode === 201) {
            this.sharePointUrl.push(response.data['downloadUrl']);
            this.fileName.push(response.data['uploadedFileName']);
          }
        }

        this.$emit('input', {
          value: this.sharePointUrl.join(','),
          id: this.documentAnswerId,
          fileName: this.fileName.join(','),
        });
        this.showUrl = true;
        this.files = [];
        this.tempFiles = [];
        this.loading = false;
      },
      getValidationState({ dirty, validated, valid = null, required }) {
        if (required) {
          return dirty || validated ? valid : null;
        }
      },
      checkSize() {
        for (let i = 0; i < this.files.length; i++) {
          if (this.files[i].size > this.acceptedFileSize) {
            this.formStatus = false;
            this.error = `The file's size is greater than 4 MB`;
            return false;
          }
        }
        this.formStatus = true;
        return true;
      },
      checkType() {
        for (let i = 0; i < this.files.length; i++) {
          if (!this.acceptedFileTypes.includes(this.files[i].type)) {
            this.formStatus = false;
            this.error = `The file's type is NOT accepted`;
            return false;
          }
        }
        this.formStatus = true;
        return true;
      },
      openDeleteWarning(item, index) {
        this.$bvModal.show('deleteModal_' + this.questionId);
        this.itemToDelete = item;
        this.itemToDeleteIndex = index;
      },
      async deleteDocument(item, index) {
        let document = new FormData();
        let endpoint;
        document.append('fileName', this.itemToDelete);
        document.append('questionId', this.questionId);
        document.append('questionName', this.name);
        document.append('eventId', this.$route.params.eventId);
        endpoint = 'delete-document';
        this.loading = true;
        await this.deleteDocumentFromSharepoint(document, endpoint, this.itemToDeleteIndex);
      },
      async deleteDocumentFromSharepoint(document, endpoint, index) {
        let canDelete = true;
        if (this.documentAnswerId !== undefined) {
          canDelete = await this.getAccessPermission();
        }
        if (!canDelete) {
          this.handleError(
            'Access error on -deleteDocumentFromSharepoint- function.',
            'You do not have access to delete this document.',
            `Access Denied`,
          );
          this.loading = false;
          return;
        }
        await axios
          .post(`/api/${endpoint}`, document)
          .then(() => {
            this.sharePointUrl.splice(index, 1);
            this.fileName.splice(index, 1);
            this.showUrl = false;
            this.$emit('input', {
              value: this.sharePointUrl.join(','),
              id: this.documentAnswerId,
              fileName: this.fileName.join(','),
            });
          })
          .catch(error => {
            this.handleError(
              error,
              'Something went wrong while deleting the document, please contact the support team',
              `Error code: (${error.response.status})`,
            );
          });
        this.loading = false;
      },
      disableInput() {
        Object.assign(this.questionClass, { hideElement: true, 'pl-5': true });
        this.disableDocument = true;
      },
      enableInput() {
        this.questionClass.hideElement = false;
        this.disableDocument = false;
      },
      async reset() {
        this.showUrl = false;
        this.tempFiles = [];
        this.files = [];
        this.sharePointUrl = [];
        this.$emit('input', { value: '', id: this.documentAnswerId, fileName: '' });
      },
      removeRequired() {
        this.isRequired = false;
      },
      enableRequired() {
        if (this.requiredQuestions.includes(this.questionId)) {
          this.isRequired = true;
        }
      },
      async checkSecurity(item) {
        if (this.documentAnswerId === undefined) {
          window.open(item, '_blank').focus();
        } else {
          window.open('/document/' + this.documentAnswerId, '_blank').focus();
        }
      },
      async getAccessPermission() {
        try {
          let response = await axios.get(`/api/get-access-permission/${this.documentAnswerId}`);
          return response.data['hasAccess'];
        } catch (error) {
          this.handleError(
            error,
            'Something went wrong while fetching the access permissions, please contact the support team.',
            `Error code: (${error.response.status})`,
          );
        }
      },
      handleError(error, message, title) {
        this.$bvToast.toast(message, {
          title: title,
          autoHideDelay: 5000,
          appendToast: true,
          variant: `danger`,
        });
        console.error(error);
        if (typeof error === 'object' && error.response.status === 422) {
          this.error = error.response.data.errors.file[0];
        }
      },
    },
  };
</script>
